import React from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Card,
    CardContent,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
    CheckCircleOutline, 
    ChevronRight, 
    ArrowRightAlt 
} from '@mui/icons-material';

// Animation variants (should be defined or imported)
const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

// Theme function (should be defined or imported)
const getTheme = (mode) => ({
    palette: {
        mode,
        primary: {
            main: mode === 'dark' ? '#90caf9' : '#1976d2'
        },
        secondary: {
            main: mode === 'dark' ? '#f48fb1' : '#d81b60'
        },
        gradients: {
            primary: mode === 'dark' 
                ? 'linear-gradient(135deg, #6a1bff 0%, #00e5ff 100%)' 
                : 'linear-gradient(135deg, #5e00ff 0%, #00e5ff 100%)'
        },
        background: {
            default: mode === 'dark' ? '#121212' : '#f5f5f5',
            paper: mode === 'dark' ? '#1e1e1e' : '#ffffff'
        },
        text: {
            primary: mode === 'dark' ? '#ffffff' : '#000000',
            secondary: mode === 'dark' ? '#b0b0b0' : '#555555'
        }
    },
    shadows: Array(25).fill('none').map((_, i) => 
        i === 8 ? (mode === 'dark' 
            ? '0px 5px 22px -5px rgba(0,0,0,0.6)' 
            : '0px 5px 22px -5px rgba(0,0,0,0.2)') 
        : 'none'
    )
});

const CaseStudies = ({ mode }) => {
    const theme = getTheme(mode);

    // Case studies data
    const caseStudies = [
        {
            title: 'Global Banking Platform',
            description: 'Developed a secure, scalable banking platform for a multinational financial institution serving 10M+ customers.',
            image: 'https://source.unsplash.com/random/600x400/?banking,finance',
            tags: ['Finance', 'Security', 'Scalability'],
            results: [
                '40% increase in transaction speed',
                '99.99% uptime',
                '50% reduction in fraud cases'
            ],
            link: '/case-studies/banking-platform'
        },
        {
            title: 'Healthcare Analytics System',
            description: 'Built an AI-powered analytics platform for a healthcare provider to improve patient outcomes and operational efficiency.',
            image: 'https://source.unsplash.com/random/600x400/?healthcare,analytics',
            tags: ['Healthcare', 'AI', 'Data Analytics'],
            results: [
                '30% improvement in diagnosis accuracy',
                '20% reduction in operational costs',
                'Improved patient satisfaction scores'
            ],
            link: '/case-studies/healthcare-analytics'
        },
        {
            title: 'Retail E-commerce Platform',
            description: 'Created a custom e-commerce solution for a retail chain that increased online sales by 150% in the first year.',
            image: 'https://source.unsplash.com/random/600x400/?ecommerce,shopping',
            tags: ['Retail', 'E-commerce', 'UX Design'],
            results: [
                '150% increase in online sales',
                '35% higher conversion rate',
                'Seamless omnichannel experience'
            ],
            link: '/case-studies/retail-ecommerce'
        },
        {
            title: 'Education Learning Platform',
            description: 'Developed a comprehensive learning management system for a university with 50,000+ students.',
            image: 'https://source.unsplash.com/random/600x400/?education,online',
            tags: ['Education', 'LMS', 'Scalability'],
            results: [
                'Improved student engagement',
                'Centralized learning resources',
                'Scalable for future growth'
            ],
            link: '/case-studies/education-platform'
        },
        {
            title: 'Manufacturing IoT System',
            description: 'Implemented an IoT solution for a manufacturing plant that reduced downtime by 45% through predictive maintenance.',
            image: 'https://source.unsplash.com/random/600x400/?manufacturing,iot',
            tags: ['Manufacturing', 'IoT', 'Predictive Analytics'],
            results: [
                '45% reduction in downtime',
                '30% increase in production efficiency',
                'Real-time equipment monitoring'
            ],
            link: '/case-studies/manufacturing-iot'
        },
        {
            title: 'Government Portal',
            description: 'Modernized a government citizen portal that improved service delivery and reduced processing times by 60%.',
            image: 'https://source.unsplash.com/random/600x400/?government,portal',
            tags: ['Government', 'Digital Transformation', 'Accessibility'],
            results: [
                '60% faster service delivery',
                'Improved citizen satisfaction',
                'Enhanced data security'
            ],
            link: '/case-studies/government-portal'
        }
    ];

    return (
        <Box sx={{
            bgcolor: 'background.default',
            color: 'text.primary',
            minHeight: '100vh',
        }}>
            {/* Hero Section */}
            <Box
                sx={{
                    pt: { xs: 8, md: 12 },
                    pb: { xs: 8, md: 6 },
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(10, 10, 26, 0.9) 0%, rgba(26, 26, 46, 0.9) 100%)'
                        : 'linear-gradient(135deg, rgba(245, 247, 255, 0.9) 0%, rgba(230, 235, 255, 0.9) 100%)',
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial="hidden"
                                animate="visible"
                                variants={fadeInUp}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{
                                        display: 'block',
                                        color: 'secondary.main',
                                        fontWeight: 600,
                                        letterSpacing: 1.5,
                                        mb: 2,
                                    }}
                                >
                                    CASE STUDIES
                                </Typography>
                                <Typography
                                    variant="h1"
                                    component="h1"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 800,
                                        mb: 3,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    Our Success Stories
                                </Typography>
                                <Typography
                                    variant="h5"
                                    color="text.secondary"
                                    paragraph
                                    sx={{ mb: 4 }}
                                >
                                    Explore how we've helped businesses across industries achieve their digital transformation goals.
                                </Typography>
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1, ease: "easeOut" }}
                            >
                                <Box
                                    component="img"
                                    src="https://source.unsplash.com/random/800x600/?success,achievement"
                                    alt="Success Stories"
                                    sx={{
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: 4,
                                        boxShadow: theme.shadows[8],
                                    }}
                                />
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Case Studies Grid */}
            <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                        variants={staggerContainer}
                    >
                        <Grid container spacing={6}>
                            {caseStudies.map((study, index) => (
                                <Grid item xs={12} md={6} lg={4} key={index}>
                                    <motion.div variants={fadeInUp}>
                                        <Card sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: theme.shadows[8],
                                            },
                                        }}>
                                            <Box
                                                component="img"
                                                src={study.image}
                                                alt={study.title}
                                                sx={{
                                                    width: '100%',
                                                    height: 200,
                                                    objectFit: 'cover',
                                                }}
                                            />
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Typography variant="h5" component="h3" gutterBottom>
                                                    {study.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" paragraph>
                                                    {study.description}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                    {study.tags.map((tag, tagIndex) => (
                                                        <Chip
                                                            key={tagIndex}
                                                            label={tag}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    ))}
                                                </Box>
                                                <List dense sx={{ mb: 2 }}>
                                                    {study.results.map((result, i) => (
                                                        <ListItem key={i} disableGutters>
                                                            <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                                                                <CheckCircleOutline fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText primary={result} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    endIcon={<ChevronRight />}
                                                    href={study.link}
                                                >
                                                    View Case Study
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box sx={{
                py: { xs: 8, md: 12 },
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(106, 27, 255, 0.1) 0%, rgba(0, 229, 255, 0.05) 100%)'
                    : 'linear-gradient(135deg, rgba(94, 0, 255, 0.05) 0%, rgba(0, 229, 255, 0.1) 100%)',
            }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <Card sx={{
                            p: { xs: 4, md: 8 },
                            borderRadius: 4,
                            background: theme.palette.gradients.primary,
                            color: '#fff',
                            boxShadow: theme.shadows[8],
                            textAlign: 'center',
                        }}>
                            <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                                Ready to Write Your Success Story?
                            </Typography>
                            <Typography variant="h5" sx={{ opacity: 0.9, mb: 6, maxWidth: 700, mx: 'auto' }}>
                                Let's discuss how we can help you achieve your business objectives with our technology solutions.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<ArrowRightAlt />}
                                sx={{
                                    bgcolor: '#fff',
                                    color: theme.palette.primary.main,
                                    py: 2,
                                    px: 6,
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    },
                                }}
                                href="/contact"
                            >
                                Contact Us
                            </Button>
                        </Card>
                    </motion.div>
                </Container>
            </Box>
        </Box>
    );
};

export default CaseStudies;